/** @jsxImportSource @emotion/react */

import css from '@emotion/css/macro'
import PropTypes from 'prop-types'
import React, { useState } from 'react'

import req from '@sportninja/common/api/request'
import colors from '@sportninja/common/constants/appColors'
import { paragraphSmall, weightSemiBold } from '../css'

/**
 * File present icon SVG component (40x40)
 */
const FilePresentIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='40'
    height='40'
    viewBox='0 0 40 40'
    fill='none'
  >
    <g clipPath='url(#clip0_signin)'>
      <path
        d='M22.2833 3.00012H10.3333C8.5 3.00012 7 4.50012 7 6.33346V33.0001C7 34.8335 8.5 36.3335 10.3333 36.3335H30.3333C32.1667 36.3335 33.6667 34.8335 33.6667 33.0001V14.3835C33.6667 13.5001 33.3167 12.6501 32.6833 12.0335L24.6333 3.98346C24.0167 3.35012 23.1667 3.00012 22.2833 3.00012ZM27 24.6668C27 28.5668 23.65 31.6835 19.6833 31.3001C16.2167 30.9668 13.6667 27.8668 13.6667 24.3835V15.7335C13.6667 13.5501 15.2333 11.5668 17.4 11.3501C19.9 11.1001 22 13.0501 22 15.5001V23.0001C22 23.9168 21.25 24.6668 20.3333 24.6668C19.4167 24.6668 18.6667 23.9168 18.6667 23.0001V15.5001C18.6667 15.0335 18.3 14.6668 17.8333 14.6668C17.3667 14.6668 17 15.0335 17 15.5001V24.4835C17 26.1501 18.1333 27.6835 19.7667 27.9501C21.8667 28.3001 23.6667 26.6835 23.6667 24.6668V19.6668C23.6667 18.7501 24.4167 18.0001 25.3333 18.0001C26.25 18.0001 27 18.7501 27 19.6668V24.6668ZM23.6667 11.3335V6.33346L30.3333 13.0001H25.3333C24.4167 13.0001 23.6667 12.2501 23.6667 11.3335Z'
        fill='white'
      />
    </g>
    <defs>
      <clipPath id='clip0_signin'>
        <rect width='40' height='40' fill='white' />
      </clipPath>
    </defs>
  </svg>
)

/**
 * File download icon SVG component (24x24)
 */
const FileDownloadIcon = ({ fill = colors.PRIMARY }) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
  >
    <path
      d='M12 15.575C11.8667 15.575 11.7417 15.554 11.625 15.512C11.5083 15.4707 11.4 15.4 11.3 15.3L7.7 11.7C7.51667 11.5167 7.425 11.2833 7.425 11C7.425 10.7167 7.51667 10.4833 7.7 10.3C7.88333 10.1167 8.12067 10.0207 8.412 10.012C8.704 10.004 8.94167 10.0917 9.125 10.275L11 12.15V5C11 4.71667 11.096 4.479 11.288 4.287C11.4793 4.09567 11.7167 4 12 4C12.2833 4 12.521 4.09567 12.713 4.287C12.9043 4.479 13 4.71667 13 5V12.15L14.875 10.275C15.0583 10.0917 15.296 10.004 15.588 10.012C15.8793 10.0207 16.1167 10.1167 16.3 10.3C16.4833 10.4833 16.575 10.7167 16.575 11C16.575 11.2833 16.4833 11.5167 16.3 11.7L12.7 15.3C12.6 15.4 12.4917 15.4707 12.375 15.512C12.2583 15.554 12.1333 15.575 12 15.575ZM6 20C5.45 20 4.97933 19.8043 4.588 19.413C4.196 19.021 4 18.55 4 18V16C4 15.7167 4.09567 15.479 4.287 15.287C4.479 15.0957 4.71667 15 5 15C5.28333 15 5.521 15.0957 5.713 15.287C5.90433 15.479 6 15.7167 6 16V18H18V16C18 15.7167 18.096 15.479 18.288 15.287C18.4793 15.0957 18.7167 15 19 15C19.2833 15 19.5207 15.0957 19.712 15.287C19.904 15.479 20 15.7167 20 16V18C20 18.55 19.8043 19.021 19.413 19.413C19.021 19.8043 18.55 20 18 20H6Z'
      fill={fill}
    />
  </svg>
)

const SignInSheetCard = ({ gameId, gameNumber, title = 'SIGN-IN SHEET' }) => {
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Handles the sign-in sheet download
   * Defaults to showing team rosters (show_team_rosters=1)
   */
  const handleDownload = async () => {
    if (!gameId) return

    try {
      setDownloading(true)
      setError(null)
      const response = await req(
        `/games/${gameId}/signin/pdf?show_team_rosters=1`,
        {
          parseJSON: false,
        }
      )
      const blob = await response.blob()
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `Game #${gameNumber || gameId} signin.pdf`
      link.click()
    } catch (e) {
      setError(
        e?.message || 'An error occurred while downloading the sign-in sheet'
      )
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div
      css={css`
        align-self: stretch;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;
        gap: 8px;
        display: inline-flex;
      `}
    >
      {/* Header */}
      <div
        css={css`
          align-self: stretch;
          justify-content: flex-start;
          align-items: center;
          display: inline-flex;
        `}
      >
        <div
          css={css`
            ${paragraphSmall}
            ${weightSemiBold}
            justify-content: center;
            display: flex;
            flex-direction: column;
            color: ${colors.NEUTRAL_50};
            text-transform: uppercase;
            line-height: 16.8px;
            letter-spacing: 1.68px;
            word-wrap: break-word;
          `}
        >
          {title}
        </div>
      </div>

      {/* Main content card */}
      <div
        css={css`
          align-self: stretch;
          padding: 8px 16px;
          background: ${colors.SECONDARY_300};
          overflow: hidden;
          border-radius: 8px;
          border: 1px solid ${colors.SECONDARY_200};
          justify-content: flex-start;
          align-items: center;
          gap: 8px;
          display: inline-flex;
        `}
      >
        {/* File icon */}
        <div
          css={css`
            width: 40px;
            height: 40px;
            position: relative;
            overflow: hidden;
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
          `}
        >
          <FilePresentIcon />
        </div>

        {/* Text content */}
        <div
          css={css`
            flex: 1 1 0;
            flex-direction: column;
            justify-content: center;
            align-items: flex-start;
            display: inline-flex;
          `}
        >
          <div
            css={css`
              align-self: stretch;
              color: ${colors.WHITE};
              font-size: 14px;
              font-family: 'Inter', Arial, Helvetica, sans-serif;
              font-weight: 700;
              line-height: 20.3px;
              word-wrap: break-word;
            `}
          >
            Sign-In Sheet
          </div>
          <div
            css={css`
              align-self: stretch;
              color: ${colors.NEUTRAL_50};
              font-size: 12px;
              font-family: 'Inter', Arial, Helvetica, sans-serif;
              font-weight: 400;
              line-height: 17.4px;
              word-wrap: break-word;
            `}
          >
            {error ? (
              <span
                css={css`
                  color: ${colors.ERROR_300};
                `}
              >
                {error}
              </span>
            ) : downloading ? (
              'Downloading...'
            ) : (
              'PDF'
            )}
          </div>
        </div>

        {/* Download icon */}
        <div
          css={css`
            width: 24px;
            height: 24px;
            position: relative;
            overflow: hidden;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: ${downloading ? 'wait' : 'pointer'};
            transition: opacity 0.2s ease-in-out;
            flex-shrink: 0;
            opacity: ${downloading ? 0.5 : 1};

            &:hover {
              opacity: ${downloading ? 0.5 : 0.8};
            }
          `}
          onClick={!downloading ? handleDownload : undefined}
          title='Download sign-in sheet'
        >
          <FileDownloadIcon fill={colors.PRIMARY} />
        </div>
      </div>
    </div>
  )
}

SignInSheetCard.propTypes = {
  gameId: PropTypes.string.isRequired,
  gameNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  title: PropTypes.string,
}

export default SignInSheetCard
